<template>
  <v-card
    id="device-info-widget"
    color="primary"
    class="full-height d-flex flex-column justify-space-between"
    :disabled="!isDeviceConnected"
  >
    <v-progress-linear
      :indeterminate="!isDeviceConnected"
      color="rgb(200, 200, 200)"
    />
    <v-card-text color="rgba(0, 0, 0, .12)" class="">
      <v-row>
        <v-col>
          <h5 class="text-h5 white--text">{{ softwareVersion }}</h5>
          <span class="text-subtitle-1 white--text"> Firmware Version </span>
        </v-col>
        <v-col>
          <h5 class="text-h5 white--text">{{ hardwareVersion }}</h5>
          <span class="text-subtitle-1 white--text"> Hardware Version </span>
        </v-col>
        <v-col>
          <h5 class="text-h5 white--text">{{ formattedParameterVersion }}</h5>
          <span class="text-subtitle-1 white--text"> API Version </span>
        </v-col>
        <v-col>
          <h5 class="text-h5 white--text text-no-wrap">{{ serialNumber }}</h5>
          <span class="text-subtitle-1 white--text"> Serial Number </span>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-title class="white--text">Device Info</v-card-title>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useApiVersion } from "@/composables/useApiVersion";
import { useFormatVersion } from "@/composables/useFormatVersion";
import { useModbusAddress } from "@/composables/useModbusAddress";
import { PortState } from "@/enums/PortState";
import { useModbusStore } from "@/stores/useModbusStore";

const NON_BREAKING_HYPHEN = `‑`;

const { apiVersion: parameterVersion } = useApiVersion();
const modbusStore = useModbusStore();
const { modbusAddress } = useModbusAddress();
const { emptyVersion, formatVersion } = useFormatVersion();

const isDeviceConnected = computed<boolean>(() => {
  return modbusStore.portState === PortState.connected;
});

const softwareVersion = computed<string>(() =>
  isDeviceConnected
    ? formatVersion(modbusStore.modbusAddrValue[modbusAddress.softwareVersion])
    : emptyVersion,
);

const hardwareVersion = computed<string>(() =>
  isDeviceConnected
    ? formatVersion(modbusStore.modbusAddrValue[modbusAddress.hardwareVersion])
    : emptyVersion,
);

const formattedParameterVersion = computed<string>(() => {
  return isDeviceConnected
    ? formatVersion(parameterVersion.value)
    : emptyVersion;
});

const serialNumber = computed<string>(() => {
  const serial = modbusStore.modbusAddrValue[modbusAddress.serialNumber];
  if (!serial) {
    return "0";
  }
  const postfix =
    modbusStore.modbusAddrValue[modbusAddress.serialNumberPostfix];
  if (!postfix) {
    return `PCS${serial}`;
  }
  return `PCS${serial}${NON_BREAKING_HYPHEN}${postfix}`;
});
</script>

<style scoped lang="scss">
.full-height {
  height: 100%;
}
</style>
